// @flow
import React from 'react';
import { AtlaskitThemeProvider } from '@atlaskit/theme';
import { WEBAPP_BASE_URL, SSO_FRAME_URL, LOGIN_URL } from 'config';
import * as IISightAPI from 'common/helpers/api';
import Loading from 'ui/pages/Loading';
import MeetingPage from 'ui/pages/Meeting';
import DialogContainer from 'ui/components/DialogContainer';
import ChromeExtensionBanner from 'jitsi/chrome-extension-banner/components/ChromeExtensionBanner.web';
import { AbstractApp } from 'jitsi/app/components/AbstractApp';
// Register middlewares and reducers.
import 'jitsi/app/middlewares.web';
import 'jitsi/app/reducers.web';
export class App extends AbstractApp {
    constructor(props) {
        super(props);
        this.state = {
            route: {},
            store: undefined,
            loaded: false,
            user: undefined,
            isHiddenUser: false,
        };
    }
    /**
     * Overrides the parent componentDidMount method.
     *
     * @override
     */
    async _extraInit() {
        // Jibri participant
        if (this._isHiddenUser()) {
            return;
        }
        if (window.location.href.includes('?code')) {
            this._checkRedirectBack();
        }
        if (window.location.pathname === '/' && window.location.search === '') {
            window.location.href = '/redirect_to_vcall';
        }
        // We should not fetch token from local storage since we can access token
        // from iframe if user has logged in Dashboard.
        if (process.env.NODE_ENV === "development") {
            const accessToken = localStorage.getItem('11S_TOKEN');
            await this._loadUser(accessToken);
        }
        let otpToken = null;
        if (!this.state.user && window.location.href.includes('otp_token')) {
            const address = new URL(window.location.href);
            otpToken = address.searchParams.get('otp_token');
        }
        if (!this.state.user) {
            await this._loginWithIframe(otpToken);
        }
        if (this._requireAuthentication()) {
            window.location.href = LOGIN_URL;
        }
        this.setState({ loaded: true });
    }
    /**
     * Overrides the parent method to inject {@link AtlaskitThemeProvider} as
     * the top most component.
     *
     * @override
     */
    _createMainElement(component, props) {
        if (this.state.loaded) {
            if (location.pathname !== '/' && !this.state.isHiddenUser) {
                return React.createElement(MeetingPage, { user: this.state.user });
            }
            return (React.createElement(AtlaskitThemeProvider, { mode: 'dark' },
                React.createElement(ChromeExtensionBanner, null),
                super._createMainElement(component, props)));
        }
        return React.createElement(Loading, null);
    }
    /**
     * Renders the platform specific dialog container.
     *
     * @returns {React$Element}
     */
    _renderDialogContainer() {
        return (React.createElement(AtlaskitThemeProvider, { mode: 'dark' },
            React.createElement(DialogContainer, { _reactionsQueue: [] })));
    }
    /**
     * Inject hidden IFrame that gets the user token.
     *
     * @returns {Promise}
     */
    _loginWithIframe(otpToken = null) {
        return new Promise((resolve) => {
            const iframe = document.createElement('iframe');
            iframe.style.display = 'none';
            const eventListener = async (event) => {
                if (event.origin !== WEBAPP_BASE_URL) {
                    return;
                }
                window.removeEventListener('message', eventListener);
                document.getElementById('sso.iframe')?.remove(); // Remove iframe
                const { token } = event.data;
                const user = await this._loadUser(token);
                if (user) {
                    localStorage.setItem('11S_TOKEN', token);
                }
                resolve();
            };
            window.addEventListener('message', eventListener, false);
            iframe.onload = () => {
                // if data not reveiced from iframe, set loaded true. Continue as anon
                setTimeout(() => {
                    window.removeEventListener('message', eventListener);
                    resolve();
                }, 5000);
            };
            if (otpToken)
                iframe.src = `${SSO_FRAME_URL}?otp_token=${otpToken}`;
            else
                iframe.src = SSO_FRAME_URL;
            iframe.id = 'sso.iframe';
            document.body.appendChild(iframe);
        });
    }
    /**
     * Loads user by given token.
     *
     * @param {string} token - OAuth Access token.
     * @returns {void}
     */
    async _loadUser(token) {
        if (!token) {
            return;
        }
        try {
            const res = await IISightAPI.getUser(token);
            const user = res.data;
            this.setState({ user });
            // TODO check this window.APP.user usage. Instead try to use Redux store.
            window.APP.user = this.state.user;
            return user;
        }
        catch (err) {
            console.error('An Error Occured While Loading The User');
        }
        return null;
    }
    _isHiddenUser() {
        // Jibri participant
        if (/JibriHeadlessChrome/.test(navigator.userAgent)) {
            this.setState({
                loaded: true,
                isHiddenUser: true,
            });
            return true;
        }
        return false;
    }
    _checkRedirectBack() {
        const redirectBack = localStorage.getItem('redirect_back');
        if (redirectBack) {
            localStorage.removeItem('redirect_back');
            window.location.href = redirectBack;
        }
        else {
            window.location.href = '/';
        }
    }
    _requireAuthentication() {
        return location.pathname === '/' && !this.state.user;
    }
    _isRoomURL() {
        return location.pathname !== '/';
    }
    _parseRoomURL() {
        let [, organisation, room] = location.pathname.split('/');
        if (organisation === 'r') {
            organisation = null;
        }
        return [organisation, room];
    }
}
